import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../api/client'
import { Badge, getStatusBadge, Loading } from '../components/ui'
import { MyProductsSEO } from '../hooks/useSEO'
import type { Product, ProductPurchase } from '../types'
import { getCategoryLabelShort } from '../utils/productLabels'

interface ListedProductsResponse {
  products: Product[]
  total: number
  page: number
  limit: number
  totalPages: number
}

interface PurchasedProductsResponse {
  purchases: ProductPurchase[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export default function MyProductsPage() {
  const [activeTab, setActiveTab] = useState<'listed' | 'purchased'>('listed')
  const [listedProducts, setListedProducts] = useState<Product[]>([])
  const [purchasedProducts, setPurchasedProducts] = useState<ProductPurchase[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionError, setActionError] = useState('')
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  useEffect(() => {
    if (activeTab === 'listed') {
      fetchListedProducts()
    } else {
      fetchPurchasedProducts()
    }
  }, [activeTab])

  const fetchListedProducts = async () => {
    setIsLoading(true)
    setError('')
    try {
      const data = await api.get<ListedProductsResponse>('/products/my/listed')
      setListedProducts(data.products)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPurchasedProducts = async () => {
    setIsLoading(true)
    setError('')
    try {
      const data = await api.get<PurchasedProductsResponse>('/products/my/purchased')
      setPurchasedProducts(data.purchases)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load purchases')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelist = async (productId: string) => {
    if (!confirm('Are you sure you want to delist this product? This will remove it from the marketplace.')) {
      return
    }
    setActionError('')
    setActionLoading(productId)
    try {
      await api.delete(`/products/${productId}`)
      await fetchListedProducts()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delist product')
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <div>
      <MyProductsSEO />
      <div className="page-header">
        <div>
          <h1>My Products</h1>
          <p className="text-secondary">Manage your listed and purchased products</p>
        </div>
        <Link to="/products/new" className="btn btn-primary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          List a Product
        </Link>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
        <button
          className={`btn ${activeTab === 'listed' ? 'btn-primary' : ''}`}
          onClick={() => setActiveTab('listed')}
        >
          Listed Products
        </button>
        <button
          className={`btn ${activeTab === 'purchased' ? 'btn-primary' : ''}`}
          onClick={() => setActiveTab('purchased')}
        >
          Purchased Products
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {actionError && <div className="alert alert-error">{actionError}</div>}

      {isLoading ? (
        <Loading message="Loading..." />
      ) : activeTab === 'listed' ? (
        listedProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              </svg>
            </div>
            <h3>No products listed</h3>
            <p>Create your first product to start selling</p>
            <Link to="/products/new" className="btn btn-primary" style={{ marginTop: '1rem' }}>
              List a Product
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {listedProducts.map((product) => {
              const statusBadge = getStatusBadge(product.status)

              return (
                <div key={product.id} className="card-static">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        <Badge status={statusBadge.variant}>{statusBadge.label}</Badge>
                        <Badge variant="blue">{getCategoryLabelShort(product.category)}</Badge>
                      </div>
                      <Link to={`/products/${product.id}`} style={{ textDecoration: 'none' }}>
                        <h3 style={{ marginBottom: '0.25rem' }}>{product.title}</h3>
                      </Link>
                      <p className="text-secondary" style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
                        {product.description.slice(0, 100)}
                        {product.description.length > 100 ? '...' : ''}
                      </p>
                      <div style={{ display: 'flex', gap: '1rem', fontSize: '13px', color: 'var(--text-secondary)' }}>
                        <span>{product.totalSales} sales</span>
                        <span>${product.totalRevenue} USDC earned</span>
                        <span>
                          {product.stockQuantity === null
                            ? 'Unlimited stock'
                            : `${product.stockQuantity} in stock`}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                      <span className="usdc-amount">${product.price} USDC</span>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => handleDelist(product.id)}
                          disabled={actionLoading === product.id}
                        >
                          {actionLoading === product.id ? 'Delisting...' : 'Delist'}
                        </button>
                        <Link to={`/products/${product.id}`} className="btn btn-sm">
                          View
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )
      ) : purchasedProducts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
          </div>
          <h3>No purchases yet</h3>
          <p>Browse the marketplace to find products</p>
          <Link to="/products" className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Browse Marketplace
          </Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {purchasedProducts.map((purchase) => (
            <div key={purchase.id} className="card-static">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Badge variant="blue">{getCategoryLabelShort(purchase.product.category)}</Badge>
                  </div>
                  <Link to={`/products/${purchase.product.id}`} style={{ textDecoration: 'none' }}>
                    <h3 style={{ marginBottom: '0.25rem' }}>{purchase.product.title}</h3>
                  </Link>
                  <p className="text-secondary" style={{ fontSize: '14px', marginBottom: '0.5rem' }}>
                    {purchase.product.description.slice(0, 100)}
                    {purchase.product.description.length > 100 ? '...' : ''}
                  </p>
                  <p className="text-secondary" style={{ fontSize: '13px' }}>
                    Purchased {new Date(purchase.createdAt).toLocaleDateString()} for ${purchase.price} USDC
                  </p>
                </div>
                <div>
                  <Link to={`/products/${purchase.product.id}`} className="btn btn-primary">
                    View Content
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
